<template>
  <div class="mx-1">
    <v-dialog v-model="dialog" max-width="400">
      <template #activator="{ on, attrs }">
        <ButtonTooltip
          color="success"
          small
          :disabled="verificationInfo.isFullyVerified"
          :loading="dayVerificationStore.isLoading"
          :block="fullWidth"
          icon-name="fa-check"
          :button-text-left="markAsVerifiedText"
          :tooltip-props="{ 'max-width': 200 }"
          v-bind="attrs"
          v-on="on"
        >
          <template #tooltip-content>
            <span v-html="tooltipNoteText" />
          </template>
        </ButtonTooltip>
      </template>

      <v-card>
        <v-card-title>{{ verificationTitle }}</v-card-title>
        <v-card-subtitle
          class="grey--text text--darken-1 mt-1"
          v-html="verificationSubtitle"
        />
        <v-card-text>
          <v-checkbox
            v-for="camera in cameras"
            :key="camera.exid"
            v-model="selectedCameras"
            :value="camera.exid"
            :disabled="
              verificationInfo.verifiedCameraExids.includes(camera.exid)
            "
            hide-details
            dense
          >
            <template #label>
              <span v-if="isVerfiedCamera(camera)">
                {{ camera.name }}
                <span class="green--text text--darken-2">
                  (<v-icon color="green" small class="mr-1">
                    fas fa-check
                  </v-icon>
                  Verified)
                </span>
              </span>
              <span v-else>
                {{ camera.name }}
              </span>
            </template>
          </v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="grey" @click="dialog = false">Cancel</v-btn>
          <v-btn
            text
            color="success"
            :disabled="!selectedCameras.length"
            @click="confirmVerification"
          >
            <v-icon color="green" small class="mr-1"> fas fa-check </v-icon>
            Verify
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useDayVerificationStore } from "@/stores/dayVerification"
import { mapStores } from "pinia"
import ButtonTooltip from "@evercam/shared/components/ButtonTooltip.vue"

export default {
  name: "MarkDayAsVerifiedButton",
  components: { ButtonTooltip },
  props: {
    selectedDate: {
      type: [String, Date],
      default: "",
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    cameras: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      selectedCameras: [],
    }
  },
  computed: {
    ...mapStores(useDayVerificationStore),
    formattedDate() {
      return this.$moment(this.selectedDate).format("YYYY-MM-DD")
    },
    verificationInfo() {
      const date = this.formattedDate
      const cameras = this.cameras

      const camerasForDay = this.dayVerificationStore.verifiedDays.filter(
        ({ day }) => day === date
      )

      const verifiedCameraExids = new Set(
        camerasForDay.map((c) => c.cameraExid)
      )

      const isFullyVerified = cameras.every((c) =>
        verifiedCameraExids.has(c.exid)
      )

      return {
        totalCameras: cameras.length,
        verifiedCameraExids: [...verifiedCameraExids],
        isFullyVerified,
      }
    },
    markAsVerifiedText() {
      return `Mark as verified (${this.verificationInfo.verifiedCameraExids.length}/${this.verificationInfo.totalCameras})`
    },
    tooltipNoteText() {
      return `
        Note: this <strong>will not have an effect on dashboard.</strong><br>
        It's meant for the admin metrics. Once a day is marked as verified, it will be highlighted in
        <strong class="green--text">green</strong> on the calendar.
      `
    },
    verificationTitle() {
      return `Daily Camera Verification — ${this.formattedDate}`
    },
    verificationSubtitle() {
      return `
        Please verify the cameras for which
        <strong>all events on the selected day have been reviewed and confirmed</strong>.
        Verified cameras will be marked and cannot be modified afterward.
      `
    },
  },
  watch: {
    dialog(value) {
      if (!value) {
        this.selectedCameras = []
      }
    },
  },
  methods: {
    async confirmVerification() {
      const confirmed = await this.$confirmDialog.open({
        title: "Confirm Verification",
        message: `
          Verify the selected camera(s) for <strong>${this.formattedDate}</strong>?<br>
          This confirms all events for that day are reviewed for the selected cameras.<br>
          <span class="deep-orange--text text--darken-2">
            <em>This action is irreversible.</em>
          </span>
        `,
      })
      if (confirmed) {
        this.$emit("verify-day", {
          date: this.formattedDate,
          camerasExid: this.selectedCameras,
        })
        this.dialog = false
      }
    },
    isVerfiedCamera(camera) {
      return this.verificationInfo.verifiedCameraExids.includes(camera.exid)
    },
  },
}
</script>
